import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import { renderTag } from 'hb-builder/components/BuilderComponents/dynamic/EmailList/EmailListLayouts/utils';
import type { EmailListLayoutsDynamicAttributes } from 'hb-builder/components/BuilderComponents/dynamic/EmailList/types';
import type { News } from 'hb-builder/components/BuilderComponents/dynamic/types/news';
import ArticleText from 'hb-react/shared/apps/Articles/ArticlePublished/ArticleText';
import ClampedLinkWorkaround from 'hb-react/shared/components/DesignSystem/ClampedLinkWorkaround';

type ListItemProps = {
  dynamicAttributes: EmailListLayoutsDynamicAttributes;
} & Omit<News, 'email_content' | 'id' | 'seo_title'>;

const ListItem = ({
  content,
  cover_picture: coverPicture,
  dynamicAttributes,
  external_url: url,
  published_at: publishedAt,
  seo_description: seoDescription,
  subject,
  tags,
  truncated_content: truncatedContent,
}: ListItemProps) => {
  const description = seoDescription || truncatedContent || content;

  const {
    displayImage,
    displayTitle,
    displayDate,
    displayDescription,
    displayTags,
    displayTextLink,
  } = dynamicAttributes.fields;

  return (
    <Wrapper>
      {displayImage && (
        <StyledImg $image={coverPicture} dataTestId="builder-cover-wrapper" />
      )}
      <Box flex={1} flexDirection="column" ml={4}>
        {displayTitle && (
          <TitleWrapper>
            <ClampedLinkWorkaround
              clamp={2}
              padding={0}
              margin={0}
              useNativeClamp
              lineHeight=""
            >
              {subject}
            </ClampedLinkWorkaround>
          </TitleWrapper>
        )}
        {displayDate && publishedAt && (
          <Box mt={2}>
            {__('Published on ')}
            {publishedAt}
          </Box>
        )}
        {displayTags && Boolean(tags?.length) && (
          <Box fontSize="12px" mt="10px">
            {tags.map((tag, i) => renderTag(tag.name, tags.length === i + 1))}
          </Box>
        )}
        {displayDescription && description && (
          <DescriptionWrapper>
            <ArticleText text={description} />
          </DescriptionWrapper>
        )}
        {displayTextLink && url && (
          <Box as="a" color="primary" href="#" mt={1}>
            {s__('Builder|Continue reading in website')}
          </Box>
        )}
      </Box>
    </Wrapper>
  );
};

export default ListItem;

const Wrapper = styled(Box).attrs(() => ({
  display: 'flex',
  flexDirection: {
    _: 'column',
    m: 'row',
  },
  mb: 8,
  width: '100%',
}))``;

const StyledImg = styled(Box).attrs(() => ({
  height: '123px',
  mb: 3,
  width: {
    s: '100%',
    m: '30%',
  },
}))<{ $image: string }>`
  background-image: ${({ $image }) => `url(${$image})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  object-fit: cover;
`;

const TitleWrapper = styled(Box).attrs(() => ({
  fontSize: '18px',
  lineHeight: '22px',
}))``;

const DescriptionWrapper = styled(Box).attrs(() => ({
  mt: '10px',
}))`
  iframe,
  img,
  video {
    display: none;
  }
`;
